/**
 * Silent access-token refresh for every portal (employee, admin, client).
 *
 * Flow:
 *   1. An in-flight request hits 401.
 *   2. The response interceptor asks tryRefresh(portal) for a fresh access
 *      token using the long-lived refresh token in localStorage.
 *   3. If refresh succeeds, the interceptor rewrites the Authorization header
 *      and re-runs the original request — the user never notices.
 *   4. If refresh fails, sessionRedirect() clears everything and bounces.
 *
 * We deduplicate concurrent refresh attempts with a per-portal Promise so a
 * flood of parallel 401s only triggers one /auth/refresh call.
 */
import axios from 'axios';

const PORTAL_CFG = {
  employee: { url: '/api/employee/auth/refresh', tokenKey: 'employee_token', refreshKey: 'employee_refresh_token' },
  admin:    { url: '/api/admin/auth/refresh',    tokenKey: 'admin_token',    refreshKey: 'admin_refresh_token' },
  client:   { url: '/api/client/auth/refresh',   tokenKey: 'client_token',   refreshKey: 'client_refresh_token' },
};

// One shared in-flight refresh Promise per portal.
const inflight = { employee: null, admin: null, client: null };

/**
 * Attempt a silent refresh for this portal. Returns the new access token on
 * success, or null on any failure. Concurrent callers share the same Promise.
 */
export function tryRefresh(portal) {
  const cfg = PORTAL_CFG[portal];
  if (!cfg) return Promise.resolve(null);
  if (inflight[portal]) return inflight[portal];

  const refreshToken = localStorage.getItem(cfg.refreshKey);
  if (!refreshToken) return Promise.resolve(null);

  // Bare axios call — do NOT use the portal's authenticated instance or we'd
  // re-enter the 401 interceptor and loop.
  inflight[portal] = axios
    .post(cfg.url, { refresh_token: refreshToken }, { timeout: 15000 })
    .then((res) => {
      const newAccess = res?.data?.token;
      if (!newAccess) return null;
      try {
        localStorage.setItem(cfg.tokenKey, newAccess);
        // The backend may rotate the refresh token — save it if provided.
        if (res.data.refresh_token) {
          localStorage.setItem(cfg.refreshKey, res.data.refresh_token);
        }
      } catch {}
      return newAccess;
    })
    .catch(() => null)
    .finally(() => {
      // Clear the slot so the next 401 wave can trigger a fresh attempt.
      inflight[portal] = null;
    });

  return inflight[portal];
}

/**
 * Store login response tokens for this portal. Login endpoints now return
 * `{ token, refresh_token, ... }`.
 */
export function storeSessionTokens(portal, response) {
  const cfg = PORTAL_CFG[portal];
  if (!cfg || !response) return;
  try {
    if (response.token) localStorage.setItem(cfg.tokenKey, response.token);
    if (response.refresh_token) localStorage.setItem(cfg.refreshKey, response.refresh_token);
  } catch {}
}
